import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import {
  PRICING_PLANS,
  MONTHLY_TRIAL_CTA,
  MONTHLY_TRIAL_DAYS,
  MONTHLY_TRIAL_SUBCOPY,
} from "@/constants/pricingPlans";
import { formatQuickProfileTagline } from "@/lib/prePaywallOnboardingStorage";
import { getLogoUrl } from "@/lib/logoUtils";
import { IconTrialTimer, IconUnlock } from "@/components/icons/BrandIcons";
import { PaywallUnlockValue } from "@/components/paywall/PaywallUnlockValue";
import {
  PAYWALL_COMPANY_HIDDEN,
  PAYWALL_MOBILE_HEADLINE,
} from "@/constants/marketingHeroCopy";

const RECOMMENDED_ID = "monthly";

/**
 * Conversion-focused mobile paywall sheet: job context + trial-first monthly plan.
 */
export function MobilePaywallSimplified({
  open,
  payload,
  checkoutLoading,
  subscribedPlan,
  onSelectPlan,
  onCloseQuiet,
  onRequestClose,
}) {
  const [moreOpen, setMoreOpen] = useState(false);

  useEffect(() => {
    if (!open) setMoreOpen(false);
  }, [open]);

  const jobTitle = typeof payload?.jobTitle === "string" ? payload.jobTitle.trim() : "";
  const company = typeof payload?.company === "string" ? payload.company.trim() : "";
  const companyLogo = typeof payload?.companyLogo === "string" ? payload.companyLogo : "";
  const quickTagline = formatQuickProfileTagline(payload?.quickProfile);

  const monthly = PRICING_PLANS.find((p) => p.id === RECOMMENDED_ID);
  const others = PRICING_PLANS.filter((p) => p.id !== RECOMMENDED_ID);
  if (!monthly) return null;

  const monthlyLoading = checkoutLoading === "monthly";
  const monthlyCurrent = subscribedPlan === "monthly";

  const renderPlanButton = (plan, opts = {}) => {
    const { compact = false } = opts;
    const isLoading = checkoutLoading === plan.id;
    const isCurrent = subscribedPlan === plan.id;
    return (
      <Button
        type="button"
        className={cn("motion-press w-full font-semibold", compact ? "h-12 text-base" : "h-14 text-lg")}
        variant={plan.highlighted ? "default" : "outline"}
        size="lg"
        disabled={isCurrent || isLoading || (checkoutLoading != null && !isLoading)}
        onClick={() => {
          if (!isCurrent) onSelectPlan(plan.id);
        }}
        data-testid={`paywall-mobile-${plan.id}`}
      >
        {isCurrent ? "Your plan" : isLoading ? "Redirecting…" : plan.cta}
      </Button>
    );
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid="paywall-mobile-simplified">
      <div className="flex shrink-0 items-center justify-end px-4 pt-1">
        <button
          type="button"
          className="inline-flex h-11 w-11 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          onClick={() => onRequestClose?.()}
          aria-label="Close"
          data-testid="paywall-mobile-close"
        >
          <X className="h-5 w-5" aria-hidden />
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-4">
        {(jobTitle || company) ? (
          <div
            className="flex items-center gap-3 rounded-xl border border-border bg-muted/20 px-3 py-2.5"
            data-testid="paywall-job-context"
          >
            {company ? (
              <img
                src={getLogoUrl(companyLogo)}
                alt=""
                className="h-10 w-10 shrink-0 rounded-lg border border-border object-cover bg-muted"
                onError={(e) => {
                  e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(company)}&background=1e3a5f&color=fff&size=40`;
                }}
              />
            ) : (
              <div
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-border bg-muted text-muted-foreground"
                aria-hidden
              >
                <IconUnlock className="h-5 w-5" />
              </div>
            )}
            <div className="min-w-0">
              {jobTitle ? (
                <p className="truncate font-heading text-base font-bold leading-snug text-foreground">{jobTitle}</p>
              ) : null}
              {company ? (
                <p className="truncate text-sm text-muted-foreground">{company}</p>
              ) : (
                <p className="truncate text-sm text-muted-foreground">{PAYWALL_COMPANY_HIDDEN}</p>
              )}
            </div>
          </div>
        ) : null}

        <p className="motion-reveal mt-5 text-center font-heading text-xl font-semibold tracking-tight text-foreground">
          {PAYWALL_MOBILE_HEADLINE}
        </p>

        <PaywallUnlockValue
          compact
          trackImpression={open}
          jobTitle={jobTitle || undefined}
          className="mt-5"
        />

        {quickTagline ? (
          <p className="mt-4 text-center text-sm font-medium leading-snug text-primary">{quickTagline}</p>
        ) : null}

        <div className="mt-6 rounded-2xl border-2 border-primary/40 bg-card p-5 shadow-sm">
          <div className="flex items-center justify-center gap-2 text-primary">
            <IconTrialTimer className="h-5 w-5" />
            <p className="text-sm font-semibold uppercase tracking-wider">
              {MONTHLY_TRIAL_DAYS}-day free trial
            </p>
          </div>
          <p className="mt-3 text-center font-heading text-3xl font-bold tracking-tight text-foreground">
            {MONTHLY_TRIAL_DAYS} days free
          </p>
          <p className="mt-2 text-center text-sm font-medium text-muted-foreground">
            {MONTHLY_TRIAL_SUBCOPY}
          </p>
          <div className="mt-4 flex flex-wrap items-baseline justify-center gap-x-1.5">
            <span className="font-heading text-2xl font-bold text-foreground">{monthly.priceLabel}</span>
            <span className="text-base text-muted-foreground">{monthly.period}</span>
          </div>
          {monthly.priceNote ? (
            <p className="mt-1.5 text-center text-xs text-muted-foreground">{monthly.priceNote}</p>
          ) : null}
        </div>

        <Collapsible open={moreOpen} onOpenChange={setMoreOpen} className="mt-5">
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="flex w-full min-h-11 items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
              data-testid="paywall-mobile-more-plans"
            >
              Other plans
              <ChevronDown
                className={cn("h-4 w-4 shrink-0 transition-transform", moreOpen && "rotate-180")}
                aria-hidden
              />
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-3 overflow-hidden">
            <div className="pt-1">
              {others.map((plan) => {
                const isCurrent = subscribedPlan === plan.id;
                return (
                  <div
                    key={plan.id}
                    className="mb-3 rounded-xl border border-border bg-card p-4 last:mb-0"
                    data-testid={`paywall-mobile-alt-${plan.id}`}
                  >
                    <p className="font-heading text-base font-bold text-foreground">{plan.title}</p>
                    <div className="mt-1 text-sm text-muted-foreground">
                      <span className="font-semibold text-foreground">{plan.priceLabel}</span>
                      {plan.period}
                      {plan.priceNoteExtra ? ` · ${plan.priceNoteExtra}` : ""}
                    </div>
                    <div className="mt-3">{renderPlanButton(plan, { compact: true })}</div>
                    {isCurrent ? (
                      <p className="mt-2 text-center text-sm text-muted-foreground">You&apos;re on this plan</p>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </CollapsibleContent>
        </Collapsible>

        <p className="mt-5 text-center text-xs text-muted-foreground">
          Secured by Stripe · Cancel anytime
          {monthly.id === "monthly" ? " · No charge today" : ""}
        </p>
        <p className="mt-1.5 text-center text-xs text-muted-foreground">
          <Link
            to="/pricing"
            className="font-medium text-primary underline-offset-2 hover:underline"
            onClick={() => onCloseQuiet?.()}
          >
            Full plan comparison
          </Link>
        </p>
      </div>

      <div className="shrink-0 border-t border-border bg-background p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
        <Button
          type="button"
          size="lg"
          className="motion-press h-14 w-full rounded-2xl text-lg font-semibold shadow-md shadow-primary/20"
          disabled={monthlyCurrent || monthlyLoading || (checkoutLoading != null && !monthlyLoading)}
          onClick={() => {
            if (!monthlyCurrent) onSelectPlan("monthly");
          }}
          data-testid="paywall-mobile-trial-sticky"
        >
          {monthlyCurrent ? "Your plan" : monthlyLoading ? "Opening secure checkout…" : MONTHLY_TRIAL_CTA}
        </Button>
      </div>
    </div>
  );
}
